import { Chip } from "@dropbox/dig-components/chip";
import { UIIcon } from "@dropbox/dig-icons";
import { CheckmarkLine, FileLine } from "@dropbox/dig-icons/assets";
import { Dropdown } from "@mirage/search/General/Dropdown";
import { filterableContentTypes } from "@mirage/shared/content-type/content-types";
import { SearchFilterType } from "@mirage/shared/search/search-filters";
import i18n from "@mirage/translations";
import { getContentTypeFilterTitle } from "./utils";

import type { ContentTypeFilter } from "@mirage/shared/search/search-filters";

const ANY_OPTION_KEY = "file_type_any";

const getFilterDefinition = (key: string, label: string) => {
  const filterDefinition = {
    id: key,
    type: SearchFilterType.ContentType,
    parameters: {
      key,
      label,
    },
  } as ContentTypeFilter;
  return filterDefinition;
};

type Props = {
  onSelect: (selection: ContentTypeFilter | undefined) => void;
  activeFilters: ContentTypeFilter[];
  selected: boolean;
  onOpenDropdown: () => void;
};

export const ContentTypeFilterDropdown = ({
  onSelect,
  activeFilters,
  selected,
  onOpenDropdown,
}: Props) => {
  const getSections = () => {
    // Option for "any", used to deselect other options
    const anyOption = {
      key: ANY_OPTION_KEY,
      title: i18n.t("file_type_filter_any_option"),
      leftAccessory:
        activeFilters.length === 0 ? <UIIcon src={CheckmarkLine} /> : <></>,
      onClick: () => onSelect(undefined),
    };

    const options = filterableContentTypes.map(({ key, label }) => {
      const filterDefinition = getFilterDefinition(key, label());
      const isSelected = activeFilters.some((afilter) => afilter.id === key);

      return {
        key,
        title: label(),
        leftAccessory: isSelected ? <UIIcon src={CheckmarkLine} /> : <></>,
        onClick: () => onSelect(filterDefinition),
      };
    });

    return [anyOption, ...options];
  };

  return (
    <Dropdown
      minWidth={200}
      onToggle={(isOpen) => isOpen && onOpenDropdown()}
      anchor={
        <Chip
          withDropdownIcon={true}
          isSelected={selected}
          selectedStyle="stroke"
          aria-label={i18n.t("filter_bar_content_type_dropdown_label")}
          data-testid={"content-type-filter-chip"}
        >
          <Chip.IconAccessory>
            <UIIcon src={FileLine} />
          </Chip.IconAccessory>
          <Chip.Content>
            {getContentTypeFilterTitle(activeFilters)}
          </Chip.Content>
        </Chip>
      }
      placement="bottom-start"
      config={{
        sections: [getSections()],
      }}
      closeOnMouseLeave={true}
    />
  );
};
